import { Axes, ChartUnitInfo } from '@/common/utils/types';
import { type DecimalPrecision } from '@/common/utils/core/number/types';
import dayjs from 'dayjs';
import { MaybeRefOrGetter, toValue } from 'vue';
import { isNil, uniq } from 'lodash-es';
import { WidgetChartData } from '@/dashboard/shared/types/widgets.types';
import { AllUnitOptionValues } from '@/dashboard/features/chartDataHandler/domain/unit/constants/unit.options';
import { UnitConverter } from '@/dashboard/features/chartDataHandler/domain/unit/base/UnitConverter';
import { useStatInfoStore } from '../stores/stat-info';
import {
  convertValueWithUnit,
  ConvertValueToUnits,
  toExponentialTrimmed,
} from './convertUnits.utils';

/* 소수점 첫 번째 유효 숫자(0이 아닌 수)의 인덱스를 반환 */
const getFirstSignificantFractionIndex = (numStr: string): number => {
  const [, fraction = ''] = numStr.replace(/,/g, '').split('.');
  const index = fraction.search(/[1-9]/);
  return index;
};

/* 소수점 자리수 맞춰서 반올림 후 의미없는 0 제거 */
const roundDecimal = (numStr: string, digits: number): string => {
  const num = Number(numStr);

  if (!isFinite(num)) return numStr;

  const factor = 10 ** digits;
  const rounded = Math.round(num * factor) / factor;

  return digits === 0
    ? String(Math.round(num))
    : rounded.toFixed(digits).replace(/\.0+$|(\.\d*?)0+$/, '$1');
};

/**
 * 소수점 옵션 사용 여부에 따라 조정된 값 반환 (지수 표기법 포함)
 * @param formattedValue 포맷팅된 값
 * @param useDecimalOption 소수점 옵션 사용 여부
 * @returns formattedValue with Scale(K, M, G..)unit
 */
export const getAdjustedFormatterValueWithDecimal = (
  formattedValue: string,
  useDecimalOption: boolean,
): string => {
  const originalFormattedValue = formattedValue;
  const parts = formattedValue.trim().split(/\s+/);
  const rawValue = parts.shift() ?? '';
  const unit = parts.join(' ');

  // 숫자 추출. 천 단위 콤마는 제거 후 파싱 (콤마가 있으면 정수부에서 잘려 소수점 반올림이 스킵됨 — ≥1000 값 raw 노출 버그)
  const match = rawValue.replace(/,/g, '').match(/^(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)(.*)$/);
  if (!match) {
    return formattedValue;
  }

  const [, numStr, scaleSuffix] = match;
  if (/[eE]/.test(numStr)) {
    return originalFormattedValue;
  }

  const vNum = Number(numStr);

  if (!Number.isFinite(vNum)) {
    return formattedValue;
  }

  if (numStr.includes('.')) {
    const firstSignificantFractionIndex = getFirstSignificantFractionIndex(numStr);

    // 1 미만이고 소수부에서 0이 아닌 유효 숫자의 인덱스가 5 이상(ex. 0.00001) -> 지수표기
    if (Math.abs(vNum) < 1 && firstSignificantFractionIndex + 1 >= 5) {
      const exp = toExponentialTrimmed(numStr);
      const value = `${exp}${scaleSuffix}`;
      return unit ? `${value} ${unit}` : value;
    }

    // 소수점 옵션 사용하지 않음 -> 자동으로 소수점 자리수 4자리 제한
    if (!useDecimalOption) {
      const roundedNumStr = roundDecimal(numStr, 4);
      // 원본에 천 단위 콤마가 있었으면 반올림 후 재적용 (콤마 없던 입력은 그대로 유지)
      const roundedValue = rawValue.includes(',')
        ? Number(roundedNumStr).toLocaleString(undefined, { maximumFractionDigits: 4 })
        : roundedNumStr;
      const value = `${roundedValue}${scaleSuffix}`;
      return unit ? `${value} ${unit}` : value;
    }
  }

  return formattedValue;
};

export const formatChartValueWithUnit = ({
  value,
  unit,
  decimal = undefined,
  toUnit = undefined,
  convertToLocaleString = false,
  preserveDecimalZeros = false,
  roundingMode,
}: {
  value: number;
  unit: string;
  decimal: number | undefined;
  toUnit?: string;
  convertToLocaleString?: boolean;
  preserveDecimalZeros?: boolean;
  roundingMode?: 'round' | 'floor' | 'ceil' | 'none';
}): string => {
  const { value: convertedValue, unit: convertedUnit } = convertValueWithUnit(
    { value, unit },
    // @ts-expect-error TODO: FIXME
    { decimal, roundingMode, toUnit },
  );

  const convertedvalueWithZeroOption =
    preserveDecimalZeros && !isNil(decimal) ? convertedValue.toFixed(decimal) : convertedValue;

  const localeValue = convertToLocaleString
    ? convertedValue.toLocaleString(undefined, {
        maximumFractionDigits: decimal ?? 20,
        minimumFractionDigits: preserveDecimalZeros ? decimal : undefined,
      })
    : convertedvalueWithZeroOption;

  const convertedUnitWithSpace = convertedUnit.includes(' ') ? convertedUnit : ` ${convertedUnit}`;

  return `${localeValue}${convertedUnitWithSpace}`;
};

export const formatYAxisWithUnit = ({
  value,
  unit,
  decimal = undefined,
  showUnit = true,
  toUnit = undefined,
  roundingMode,
}: {
  value: number;
  unit: string;
  decimal?: number;
  showUnit?: boolean;
  toUnit?: ConvertValueToUnits;
  roundingMode?: 'round' | 'floor' | 'ceil' | 'none';
}): string => {
  if (!showUnit) {
    const { value: convertedValue } = convertValueWithUnit(
      { value, unit },
      { decimal, roundingMode, toUnit },
    );
    return `${convertedValue}`;
  }

  return formatChartValueWithUnit({ value, unit, decimal, toUnit });
};

export const formatTooltipWithUnit = ({
  value,
  unit,
  decimal = undefined,
  preserveDecimalZeros = false,
  roundingMode,
}: {
  value: number;
  unit: string;
  decimal?: number;
  preserveDecimalZeros?: boolean;
  roundingMode?: 'round' | 'floor' | 'ceil' | 'none';
}): string => {
  return formatChartValueWithUnit({
    value,
    unit,
    decimal,
    convertToLocaleString: true,
    preserveDecimalZeros,
    roundingMode,
  });
};

type FormatterConfigInfo = {
  interval: string | { time: number; unit: string };
  timeFormat: string;
};
export const useChartAxesFormatter = () => {
  /**
   * 시간 축 포맷터
   * @param interval   ex) 'second', 'minute', 'hour', 'day'
   * @param timeFormat  ex) 'HH:mm:ss', 'HH:mm', 'DD HH:mm'
   */
  const getXAxesFormatter = ({ interval, timeFormat }: FormatterConfigInfo): Axes['formatter'] => {
    const intervalUnit = typeof interval === 'string' ? interval : interval.unit;

    return (value: number | string, data: { prev: number | null }): string => {
      const currMonth = dayjs(value).format('YY-MM');
      const prevMonth = data?.prev ? dayjs(data.prev).format('YY-MM') : '';
      if (prevMonth && currMonth !== prevMonth) {
        return dayjs(value).format('YY/MM/DD');
      }
      if (intervalUnit !== 'day') {
        const curr = dayjs(value).format('YY-MM-DD');
        const prev = data?.prev ? dayjs(data.prev).format('YY-MM-DD') : '';
        if (curr !== prev) {
          const format = intervalUnit === 'hour' ? 'HH:mm' : timeFormat;
          return dayjs(value).format(`DD ${format}`);
        }
      }
      return dayjs(value).format(timeFormat);
    };
  };

  return {
    getXAxesFormatter,
  };
};

/**
 * X축 시간 포맷터 (tz-aware).
 * `useChartAxesFormatter` 와 달리 EvChart 가 formatter 에 넘기는 epoch ms 를
 * 사용자 설정 timezone 으로 해석한다. 데이터의 X 값이 진짜 UTC ms 인 경우에 사용.
 *
 * @param interval ex) 'second', 'minute', 'hour', 'day' 또는 { time, unit }
 * @param timeFormat ex) 'HH:mm:ss', 'HH:mm', 'DD HH:mm'
 */
export const getTzXAxesFormatter = ({
  interval,
  timeFormat,
}: FormatterConfigInfo): Axes['formatter'] => {
  const intervalUnit = typeof interval === 'string' ? interval : interval.unit;
  const dayFormat = intervalUnit === 'hour' ? 'HH:mm' : timeFormat;

  return (value: number | string, data: { prev: number | null }): string => {
    const curr = dayjs.tz(value);
    const prev = data?.prev ? dayjs.tz(data.prev) : null;

    if (prev && curr.format('YY-MM') !== prev.format('YY-MM')) {
      return curr.format('YY/MM/DD');
    }
    if (intervalUnit !== 'day' && curr.format('YY-MM-DD') !== prev?.format('YY-MM-DD')) {
      return curr.format(`DD ${dayFormat}`);
    }
    return curr.format(timeFormat);
  };
};

export const useEvChartTooltipFormatter = (
  unitList: MaybeRefOrGetter<ChartUnitInfo[]>,
  decimal?: MaybeRefOrGetter<DecimalPrecision | undefined>,
  preserveDecimalZeros?: MaybeRefOrGetter<boolean>,
  isHorizontal = false,
  roundingMode?: 'round' | 'floor' | 'ceil' | 'none',
) => {
  const tooltipValueFormatter = ({ seriesId, x, y, customUnit = '' }) => {
    const value = isHorizontal ? x : y;
    let formattedValue = '';

    let unit: string | null = null;

    if (customUnit !== '') {
      unit = customUnit;
    } else {
      unit = toValue(unitList).find((unitInfo) => unitInfo.seriesId === seriesId)?.unit ?? '';
    }

    if (unit) {
      formattedValue = formatTooltipWithUnit({
        value,
        unit,
        decimal: toValue(decimal),
        preserveDecimalZeros: toValue(preserveDecimalZeros),
        roundingMode,
      });
    } else {
      formattedValue = value.toLocaleString(undefined, {
        maximumFractionDigits: toValue(decimal),
        minimumFractionDigits: toValue(preserveDecimalZeros) ? toValue(decimal) : undefined,
      });
    }

    return getAdjustedFormatterValueWithDecimal(formattedValue, !isNil(toValue(decimal)));
  };

  const markerValueFormatter = (value: number, unit?: string) => {
    let formattedValue = '';
    if (unit && unit.toLowerCase() !== 'count') {
      formattedValue = formatTooltipWithUnit({
        value,
        unit,
        decimal: toValue(decimal),
        preserveDecimalZeros: toValue(preserveDecimalZeros),
      });
    } else {
      formattedValue = value.toLocaleString(undefined, {
        maximumFractionDigits: toValue(decimal),
        minimumFractionDigits: toValue(preserveDecimalZeros) ? toValue(decimal) : undefined,
      });
    }

    return getAdjustedFormatterValueWithDecimal(formattedValue, !isNil(toValue(decimal)));
  };

  return {
    tooltipValueFormatter,
    markerValueFormatter,
  };
};

export const useDefaultUnit = () => {
  const statInfoStore = useStatInfoStore();

  const getDefaultUnit = (
    dataId: string,
    childCategory: string,
    defaultUnit: AllUnitOptionValues = 'none',
  ): AllUnitOptionValues => {
    if (!dataId) {
      return defaultUnit;
    }
    const statUnit = statInfoStore.getStatInfo({ statId: dataId, childCategory })?.unit;
    if (statUnit) {
      return UnitConverter.convertApiUnit(statUnit) ?? defaultUnit;
    }
    return defaultUnit;
  };

  const getFormulaDefaultUnit = (
    formula: string,
    chartDataList: WidgetChartData[],
    defaultUnit: AllUnitOptionValues = 'count',
  ): AllUnitOptionValues => {
    const A = 'A'.charCodeAt(0);
    const usedChar = Array.from(new Set(formula.toUpperCase().match(/[A-Z]/g)));
    const usedIndex = usedChar.map((char) => char.charCodeAt(0) - A);
    const units = usedIndex
      .map((index) => {
        const chartData = chartDataList.at(index);
        return chartData
          ? getDefaultUnit(chartData.dataId, chartData.category, defaultUnit)
          : defaultUnit;
      })
      .filter(Boolean);
    return uniq(units).length === 1 ? (units[0] ?? defaultUnit) : defaultUnit;
  };

  return {
    getDefaultUnit,
    getFormulaDefaultUnit,
  };
};
